/**
 * Runtime club-colour derivation.
 *
 * A club's submitted colours are identity data, not trusted CSS. This module
 * accepts only an exact six-digit HEX seed, derives accessible interaction
 * shades for each viewer theme, and returns a closed set of CSS variables for
 * the root brand synchronizer (or a scoped settings preview) to apply.
 */

type Rgb = readonly [number, number, number];

export type ClubBrandThemeTokenBundle = Readonly<{
  accent: string;
  accentRgb: string;
  accentHover: string;
  accentHoverRgb: string;
  accentInk: string;
  accentInkRgb: string;
  accentText: string;
  accentTextRgb: string;
  focusRing: string;
  focusRingRgb: string;
}>;

export type ClubNavigationTokens = Readonly<{
  background: string;
  hover: string;
  ink: string;
  active: string;
  activeInk: string;
  focus: string;
}>;

export type ClubBrandTokenBundle = Readonly<{
  primary: string;
  secondary: string | null;
  navigation: ClubNavigationTokens;
  light: ClubBrandThemeTokenBundle;
  dark: ClubBrandThemeTokenBundle;
}>;

type ThemeSpec = Readonly<{
  surfaces: readonly Rgb[];
  accentInk: Rgb;
  hoverDirection: -1 | 1;
}>;

const TKTIC_NAVY: Rgb = [11, 31, 58];
const TKTIC_PAPER: Rgb = [246, 244, 239];

const THEME_SPECS: Readonly<Record<"light" | "dark", ThemeSpec>> = {
  light: {
    surfaces: [
      [246, 244, 239], // canvas
      [255, 255, 255], // surface
      [250, 250, 247], // elevated
    ],
    accentInk: TKTIC_PAPER,
    hoverDirection: -1,
  },
  dark: {
    surfaces: [
      [5, 16, 38], // canvas
      [16, 37, 67], // surface
      [21, 46, 81], // elevated
    ],
    accentInk: TKTIC_NAVY,
    hoverDirection: 1,
  },
};

/** Highest supported semantic accent wash in current application chrome. */
const ACCENT_WASH_ALPHAS = [0.1, 0.15, 0.2, 0.25] as const;
const TEXT_CONTRAST = 4.5;

export const BRAND_CSS_VARIABLES = [
  "--club-brand-primary",
  "--club-brand-secondary",
  "--club-brand-nav-background",
  "--club-brand-nav-hover",
  "--club-brand-nav-ink",
  "--club-brand-nav-active",
  "--club-brand-nav-active-ink",
  "--club-brand-nav-focus",
  "--club-brand-light-accent",
  "--club-brand-light-accent-rgb",
  "--club-brand-light-accent-hover",
  "--club-brand-light-accent-hover-rgb",
  "--club-brand-light-accent-ink",
  "--club-brand-light-accent-ink-rgb",
  "--club-brand-light-accent-text",
  "--club-brand-light-accent-text-rgb",
  "--club-brand-light-focus-ring",
  "--club-brand-light-focus-ring-rgb",
  "--club-brand-dark-accent",
  "--club-brand-dark-accent-rgb",
  "--club-brand-dark-accent-hover",
  "--club-brand-dark-accent-hover-rgb",
  "--club-brand-dark-accent-ink",
  "--club-brand-dark-accent-ink-rgb",
  "--club-brand-dark-accent-text",
  "--club-brand-dark-accent-text-rgb",
  "--club-brand-dark-focus-ring",
  "--club-brand-dark-focus-ring-rgb",
] as const;

/** Return canonical uppercase HEX, or null for empty/hostile values. */
export function normalizeBrandHex(value: unknown): string | null {
  if (typeof value !== "string" || !/^#[0-9A-Fa-f]{6}$/.test(value)) {
    return null;
  }
  return value.toUpperCase();
}

/** WCAG contrast ratio; exported so the admin preview can test its own UI. */
export function brandColorContrast(first: string, second: string): number {
  const a = parseHex(first);
  const b = parseHex(second);
  if (!a || !b) return 0;
  const aLuminance = relativeLuminance(a);
  const bLuminance = relativeLuminance(b);
  const [lighter, darker] =
    aLuminance >= bLuminance
      ? [aLuminance, bLuminance]
      : [bLuminance, aLuminance];
  return (lighter + 0.05) / (darker + 0.05);
}

export function deriveBrandColorTokens(
  primaryValue: unknown,
  secondaryValue: unknown = "",
): ClubBrandTokenBundle | null {
  const primary = normalizeBrandHex(primaryValue);
  if (!primary) return null;

  // A malformed stored secondary must not block an otherwise valid primary.
  // It resolves to empty, matching the server's own-or-empty read contract.
  const secondary = normalizeBrandHex(secondaryValue);
  const seed = parseHex(primary);
  if (!seed) return null;

  return {
    primary,
    secondary,
    navigation: deriveNavigationBundle(seed, parseHex(secondary ?? primary)!),
    light: deriveThemeBundle(seed, THEME_SPECS.light),
    dark: deriveThemeBundle(seed, THEME_SPECS.dark),
  };
}

/**
 * Closed namespaced variable map. Callers set these with style.setProperty;
 * settings values are never interpolated into a stylesheet or raw CSS text.
 */
export function brandColorCssVariables(
  bundle: ClubBrandTokenBundle,
): Readonly<Record<string, string>> {
  const variables: Record<string, string> = {
    "--club-brand-primary": bundle.primary,
    "--club-brand-nav-background": bundle.navigation.background,
    "--club-brand-nav-hover": bundle.navigation.hover,
    "--club-brand-nav-ink": bundle.navigation.ink,
    "--club-brand-nav-active": bundle.navigation.active,
    "--club-brand-nav-active-ink": bundle.navigation.activeInk,
    "--club-brand-nav-focus": bundle.navigation.focus,
  };
  if (bundle.secondary) {
    variables["--club-brand-secondary"] = bundle.secondary;
  }

  for (const theme of ["light", "dark"] as const) {
    const tokens = bundle[theme];
    const prefix = `--club-brand-${theme}`;
    variables[`${prefix}-accent`] = tokens.accent;
    variables[`${prefix}-accent-rgb`] = tokens.accentRgb;
    variables[`${prefix}-accent-hover`] = tokens.accentHover;
    variables[`${prefix}-accent-hover-rgb`] = tokens.accentHoverRgb;
    variables[`${prefix}-accent-ink`] = tokens.accentInk;
    variables[`${prefix}-accent-ink-rgb`] = tokens.accentInkRgb;
    variables[`${prefix}-accent-text`] = tokens.accentText;
    variables[`${prefix}-accent-text-rgb`] = tokens.accentTextRgb;
    variables[`${prefix}-focus-ring`] = tokens.focusRing;
    variables[`${prefix}-focus-ring-rgb`] = tokens.focusRingRgb;
  }
  return variables;
}

/** Header stays dark in either viewer theme, with a restrained primary tint.
 * Secondary supplies only the active navigation tint, never action colours.
 * Keep paper artwork/utility icons readable even for white, black or yellow
 * seeds. Header focus has its own pair: the light-page ring may be too dark.
 */
function deriveNavigationBundle(
  primary: Rgb,
  secondary: Rgb,
): ClubNavigationTokens {
  const primaryHsl = rgbToHsl(primary);
  const secondaryHsl = rgbToHsl(secondary);
  const background = hslToRgb(
    primaryHsl.hue,
    primaryHsl.saturation * 0.7,
    0.13,
  );
  const hover = blend(TKTIC_PAPER, background, 0.08);
  const saturation = secondaryHsl.saturation * 0.6;
  let active = hslToRgb(secondaryHsl.hue, saturation, 0.27);
  // Retain an accessible paper fallback while finding a softer secondary ink.
  for (
    let step = 270;
    contrastRgb(TKTIC_PAPER, active) < TEXT_CONTRAST && step > 0;
    step -= 1
  ) {
    active = hslToRgb(secondaryHsl.hue, saturation, step / 1000);
  }
  let activeInk = TKTIC_PAPER;
  for (let step = 800; step <= 1000; step += 1) {
    const candidate = hslToRgb(
      secondaryHsl.hue,
      secondaryHsl.saturation * 0.7,
      step / 1000,
    );
    if (contrastRgb(candidate, active) >= TEXT_CONTRAST) {
      activeInk = candidate;
      break;
    }
  }
  return {
    background: rgbToHex(background),
    hover: rgbToHex(hover),
    ink: rgbToHex(TKTIC_PAPER),
    active: rgbToHex(active),
    activeInk: rgbToHex(activeInk),
    focus: rgbToHex(TKTIC_PAPER),
  };
}

function deriveThemeBundle(
  seed: Rgb,
  spec: ThemeSpec,
): ClubBrandThemeTokenBundle {
  const seedHsl = rgbToHsl(seed);
  const accentRgb = findAccessibleShade(seedHsl, seedHsl.lightness, spec);
  const accentHsl = rgbToHsl(accentRgb);
  const desiredHoverLightness = clamp(
    accentHsl.lightness + spec.hoverDirection * 0.06,
    0,
    1,
  );
  const hoverRgb = findAccessibleShade(
    {
      hue: seedHsl.hue,
      saturation: seedHsl.saturation,
      lightness: accentHsl.lightness,
    },
    desiredHoverLightness,
    spec,
  );

  const accent = rgbToHex(accentRgb);
  const accentHover = rgbToHex(hoverRgb);
  const accentInk = rgbToHex(spec.accentInk);
  return {
    accent,
    accentRgb: rgbChannels(accentRgb),
    accentHover,
    accentHoverRgb: rgbChannels(hoverRgb),
    accentInk,
    accentInkRgb: rgbChannels(spec.accentInk),
    // The derived fill also meets body-text contrast on every supported
    // surface/wash, so existing `text-accent` remains safe while the named
    // text role is available to new components and previews.
    accentText: accent,
    accentTextRgb: rgbChannels(accentRgb),
    focusRing: accent,
    focusRingRgb: rgbChannels(accentRgb),
  };
}

type Hsl = Readonly<{
  hue: number;
  saturation: number;
  lightness: number;
}>;

function findAccessibleShade(
  seed: Hsl,
  preferredLightness: number,
  spec: ThemeSpec,
): Rgb {
  let winner: Rgb | null = null;
  let winnerDistance = Number.POSITIVE_INFINITY;

  // One-thousandth lightness steps are deterministic and substantially finer
  // than an 8-bit RGB channel. The loop is tiny and runs only when settings
  // resolve or change, never during paint/animation.
  for (let step = 0; step <= 1000; step += 1) {
    const lightness = step / 1000;
    const candidate = hslToRgb(seed.hue, seed.saturation, lightness);
    if (!meetsThemeContrast(candidate, spec)) continue;
    const distance = Math.abs(lightness - preferredLightness);
    if (distance < winnerDistance) {
      winner = candidate;
      winnerDistance = distance;
    }
  }

  // Black/paper and white/navy are guaranteed endpoint pairs, but retain a
  // defensive fallback if the theme matrix is extended incorrectly later.
  return winner ?? (spec.hoverDirection === -1 ? [0, 0, 0] : [255, 255, 255]);
}

function meetsThemeContrast(candidate: Rgb, spec: ThemeSpec): boolean {
  if (contrastRgb(candidate, spec.accentInk) < TEXT_CONTRAST) return false;
  for (const surface of spec.surfaces) {
    if (contrastRgb(candidate, surface) < TEXT_CONTRAST) return false;
    for (const alpha of ACCENT_WASH_ALPHAS) {
      const wash = blend(candidate, surface, alpha);
      if (contrastRgb(candidate, wash) < TEXT_CONTRAST) return false;
    }
  }
  return true;
}

function parseHex(value: string): Rgb | null {
  if (!/^#[0-9A-Fa-f]{6}$/.test(value)) return null;
  return [
    Number.parseInt(value.slice(1, 3), 16),
    Number.parseInt(value.slice(3, 5), 16),
    Number.parseInt(value.slice(5, 7), 16),
  ];
}

function rgbToHex(rgb: Rgb): string {
  return `#${rgb
    .map((channel) => Math.round(channel).toString(16).padStart(2, "0"))
    .join("")}`.toUpperCase();
}

function rgbChannels(rgb: Rgb): string {
  return rgb.map((channel) => Math.round(channel)).join(" ");
}

function srgbChannel(value: number): number {
  const channel = value / 255;
  return channel <= 0.03928
    ? channel / 12.92
    : Math.pow((channel + 0.055) / 1.055, 2.4);
}

function relativeLuminance(rgb: Rgb): number {
  return (
    0.2126 * srgbChannel(rgb[0]) +
    0.7152 * srgbChannel(rgb[1]) +
    0.0722 * srgbChannel(rgb[2])
  );
}

function contrastRgb(first: Rgb, second: Rgb): number {
  const firstLuminance = relativeLuminance(first);
  const secondLuminance = relativeLuminance(second);
  const [lighter, darker] =
    firstLuminance >= secondLuminance
      ? [firstLuminance, secondLuminance]
      : [secondLuminance, firstLuminance];
  return (lighter + 0.05) / (darker + 0.05);
}

function blend(foreground: Rgb, background: Rgb, alpha: number): Rgb {
  return [
    Math.round(foreground[0] * alpha + background[0] * (1 - alpha)),
    Math.round(foreground[1] * alpha + background[1] * (1 - alpha)),
    Math.round(foreground[2] * alpha + background[2] * (1 - alpha)),
  ];
}

function rgbToHsl([red, green, blue]: Rgb): Hsl {
  const r = red / 255;
  const g = green / 255;
  const b = blue / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  const lightness = (max + min) / 2;
  if (delta === 0) return { hue: 0, saturation: 0, lightness };

  const saturation =
    lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
  let hue: number;
  if (max === r) {
    hue = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    hue = ((b - r) / delta + 2) / 6;
  } else {
    hue = ((r - g) / delta + 4) / 6;
  }
  return { hue, saturation, lightness };
}

function hslToRgb(hue: number, saturation: number, lightness: number): Rgb {
  if (saturation === 0) {
    const channel = Math.round(lightness * 255);
    return [channel, channel, channel];
  }

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;
  const channel = (offset: number) => {
    let t = hue + offset;
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  return [
    Math.round(channel(1 / 3) * 255),
    Math.round(channel(0) * 255),
    Math.round(channel(-1 / 3) * 255),
  ];
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
